import { Badge } from "@/components/ui/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { QA_RULES, hasQAReview } from "@/types/qaReview";
import type { QAReviewWithContent } from "@/types/qaReview";
import { cn } from "@/lib/utils";

interface QAProgressBadgeProps {
	review: QAReviewWithContent | null | undefined;
}

export function QAProgressBadge({ review }: QAProgressBadgeProps) {
	if (!review) {
		return (
			<Badge variant="outline" className="text-muted-foreground font-normal">
				—
			</Badge>
		);
	}

	// Post has no QA review record yet
	if (!hasQAReview(review)) {
		return (
			<Badge variant="outline" className="text-muted-foreground font-normal">
				Not Reviewed
			</Badge>
		);
	}

	const issueCount = QA_RULES.reduce(
		(count, rule) => count + (review[rule.key] === true ? 1 : 0),
		0,
	);

	const flaggedRules = QA_RULES.filter((rule) => review[rule.key] === true);

	const tooltipContent =
		issueCount === 0
			? "All checks passed"
			: flaggedRules.map((r) => r.shortLabel).join(", ");

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Badge
					className={cn(
						"font-normal cursor-default",
						issueCount === 0
							? "bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-500/20"
							: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-500/20",
					)}
				>
					{issueCount} issue{issueCount !== 1 ? "s" : ""}
				</Badge>
			</TooltipTrigger>
			<TooltipContent side="top" className="max-w-[250px]">
				<p className="text-xs">{tooltipContent}</p>
			</TooltipContent>
		</Tooltip>
	);
}
